/**
 * Chat History Sidebar Component
 *
 * Displays chat session history with ability to create new chats,
 * select existing sessions, and delete sessions.
 * Layout similar to Claude's sidebar.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  CircularProgress,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import { ChatSession } from '../types';
import sessionService from '../services/session.service';

interface ChatHistorySidebarProps {
  currentSessionId: string | null;
  projectId?: string;
  onNewChat: () => void;
  onSelectSession: (session: ChatSession) => void;
  onDeleteSession: (sessionId: string) => void;
  refreshTrigger?: number;
}

const ChatHistorySidebar: React.FC<ChatHistorySidebarProps> = ({
  currentSessionId,
  projectId,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  refreshTrigger,
}) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch sessions on mount and when projectId or refreshTrigger changes
  useEffect(() => {
    fetchSessions();
  }, [projectId, refreshTrigger]);

  const fetchSessions = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Always load the full list; "Search chats" filters it client-side as
      // the user types (see visibleSessions), so no round-trip per keystroke.
      const response = await sessionService.listSessions(projectId, undefined, 1, 100);
      setSessions(response.sessions);
    } catch (err: any) {
      console.error('Failed to fetch sessions:', err);
      setError(err.message || 'Failed to load chat history');
    } finally {
      setIsLoading(false);
    }
  };

  // Case-insensitive substring match on the title (and the last-message
  // preview, which is what the list shows under the title).
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const visibleSessions = normalizedQuery
    ? sessions.filter((s) =>
        (s.title || 'New Session').toLowerCase().includes(normalizedQuery) ||
        (s.last_message_preview || '').toLowerCase().includes(normalizedQuery)
      )
    : sessions;

  const handleDelete = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    try {
      await sessionService.deleteSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      onDeleteSession(sessionId);
    } catch (err: any) {
      console.error('Failed to delete session:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const groupSessionsByDate = (sessions: ChatSession[]) => {
    const groups: { [key: string]: ChatSession[] } = {
      Today: [],
      Yesterday: [],
      'Previous 7 Days': [],
      Older: [],
    };

    const now = new Date();

    sessions.forEach((session) => {
      const date = new Date(session.updated_at);
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        groups['Today'].push(session);
      } else if (diffDays === 1) {
        groups['Yesterday'].push(session);
      } else if (diffDays < 7) {
        groups['Previous 7 Days'].push(session);
      } else {
        groups['Older'].push(session);
      }
    });

    return groups;
  };

  const groupedSessions = groupSessionsByDate(visibleSessions);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.50',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      {/* New Chat Button */}
      <Box sx={{ p: 2 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{
            py: 1.5,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          New Chat
        </Button>
      </Box>

      {/* Search */}
      <Box sx={{ px: 2, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search chats..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          inputProps={{ 'aria-label': 'Search chats' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white',
            },
          }}
        />
      </Box>

      <Divider />

      {/* Sessions List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
            <Button size="small" onClick={fetchSessions} sx={{ mt: 1 }}>
              Retry
            </Button>
          </Box>
        ) : sessions.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <ChatIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No chat history yet
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Start a new chat to begin
            </Typography>
          </Box>
        ) : visibleSessions.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No chats match &ldquo;{searchQuery.trim()}&rdquo;
            </Typography>
            <Button size="small" onClick={() => setSearchQuery('')} sx={{ mt: 1 }}>
              Clear search
            </Button>
          </Box>
        ) : (
          <List disablePadding>
            {Object.entries(groupedSessions).map(([groupName, groupSessions]) =>
              groupSessions.length > 0 ? (
                <React.Fragment key={groupName}>
                  <Box sx={{ px: 2, py: 1, bgcolor: 'grey.100' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={600}
                    >
                      {groupName}
                    </Typography>
                  </Box>
                  {groupSessions.map((session) => (
                    <ListItem
                      key={session.id}
                      disablePadding
                      secondaryAction={
                        <Tooltip title="Delete chat">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => handleDelete(e, session.id)}
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              '.MuiListItem-root:hover &': {
                                opacity: 1,
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      }
                      sx={{
                        '&:hover .MuiIconButton-root': {
                          opacity: 1,
                        },
                      }}
                    >
                      <ListItemButton
                        selected={currentSessionId === session.id}
                        onClick={() => onSelectSession(session)}
                        sx={{
                          py: 1.5,
                          '&.Mui-selected': {
                            bgcolor: 'primary.light',
                            '&:hover': {
                              bgcolor: 'primary.light',
                            },
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight:
                                  currentSessionId === session.id ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                pr: 4,
                              }}
                            >
                              {session.title || 'New Session'}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                              {session.last_message_preview ||
                                `${session.message_count} messages`}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </React.Fragment>
              ) : null
            )}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default ChatHistorySidebar;
